import { useCallback, useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import {
  getMergeConstraints,
  getMissingRelationshipOwnerInfo,
  getPerson,
  mergePersons,
} from '../../../services/personService'
import { isAlphaNumericPersonId } from '../../../util/EntityUtil'
import { getLastPathElement } from '../../../util/StringUtil'
import { deepCopy } from '../../../util/Utils'

// constants
const actions = {
  ACTION_BUTTON_ON_CLICK: 'ACTION_BUTTON_ON_CLICK',
  CHANGE_MESSAGE: 'CHANGE_MESSAGE',
  LIFE_SKETCH_EXPANDED_HEIGHT: 'LIFE_SKETCH_EXPANDED_HEIGHT',
  MERGE_CONSTRAINT: 'MERGE_CONSTRAINT',
  MERGE_SETUP: 'MERGE_SETUP',
  MERGED_FIELD_IDS: 'MERGED_FIELD_IDS',
  POSSIBLE_DUPLICATE: 'POSSIBLE_DUPLICATE',
  POSSIBLE_DUPLICATE_INPUT: 'POSSIBLE_DUPLICATE_INPUT',
  SHOW_MERGE_DATA: 'SHOW_MERGE_DATA',
  SHOW_MERGE_SUCCESS_MESSAGE: 'SHOW_MERGE_SUCCESS_MESSAGE',
  SURVIVING_PERSON: 'SURVIVING_PERSON',
  SURVIVING_PERSON_INPUT: 'SURVIVING_PERSON_INPUT',
  TOGGLE_SHOW_ITEM_HIDDEN_DETAILS: 'TOGGLE_SHOW_ITEM_HIDDEN_DETAILS',
  TOGGLE_SHOW_MORE_LESS_LIFE_SKETCH: 'TOGGLE_SHOW_MORE_LESS_LIFE_SKETCH',
  WAITING_FOR_PERSON: 'WAITING_FOR_PERSON',
}
const actionButtonTypes = {
  HIDDEN: 'HIDDEN',
  NONE: 'NONE',
  REPLACE: 'REPLACE',
  UNDO: 'UNDO',
}

const itemDisplayStatus = {
  HIDDEN: 'HIDDEN',
  REMOVE: 'REMOVE',
  SHOW: 'SHOW',
}

function adminMergeReducer(oldState, action) {
  const newState = deepCopy(oldState)
  const { payload, type } = action
  if (type === actions.ACTION_BUTTON_ON_CLICK) {
    actionButtonOnClickHelper(newState, payload)
  } else if (type === actions.CHANGE_MESSAGE) {
    newState.mergedFieldIds.attribution.changeMessage = payload
  } else if (type === actions.LIFE_SKETCH_EXPANDED_HEIGHT) {
    const { height, isSurvivingPerson } = payload
    if (isSurvivingPerson) {
      newState.merge.possibleDuplicate.lifesketch.otherExpandedHeight = height
      newState.merge.survivingPerson.lifesketch.expandedHeight = height
    } else {
      newState.merge.survivingPerson.lifesketch.otherExpandedHeight = height
      newState.merge.possibleDuplicate.lifesketch.expandedHeight = height
    }
  } else if (type === actions.MERGE_CONSTRAINT) {
    newState.mergeConstraint = payload
  } else if (type === actions.MERGE_SETUP) {
    mergeSetup(newState, newState.possibleDuplicate, newState.survivingPerson)
  } else if (type === actions.MERGED_FIELD_IDS) {
    newState.mergedFieldIds = payload
  } else if (type === actions.POSSIBLE_DUPLICATE) {
    newState.possibleDuplicate = payload
    newState.possibleDuplicateId = payload?.id
  } else if (type === actions.POSSIBLE_DUPLICATE_INPUT) {
    newState.possibleDuplicateInput = payload
  } else if (type === actions.SHOW_MERGE_DATA) {
    newState.showMergeData = payload
  } else if (type === actions.SHOW_MERGE_SUCCESS_MESSAGE) {
    newState.showMergeSuccessMessage = payload
  } else if (type === actions.SURVIVING_PERSON) {
    newState.survivingPerson = payload
    newState.survivingPersonId = payload?.id
  } else if (type === actions.SURVIVING_PERSON_INPUT) {
    newState.survivingPersonInput = payload
  } else if (type === actions.TOGGLE_SHOW_ITEM_HIDDEN_DETAILS) {
    toggleShowItemHiddenDetailsHelper(newState, payload)
  } else if (type === actions.TOGGLE_SHOW_MORE_LESS_LIFE_SKETCH) {
    newState.merge.possibleDuplicate.lifesketch.showMore = !newState.merge.possibleDuplicate.lifesketch.showMore
    newState.merge.survivingPerson.lifesketch.showMore = !newState.merge.survivingPerson.lifesketch.showMore
  } else if (type === actions.WAITING_FOR_PERSON) {
    newState.waitingForPerson = payload
  }
  return newState
}

export default function useAdminMerge() {
  const { error, ok } = useNotifications()
  const { t } = useTranslation()

  // State
  const initialState = {
    // controls
    showMergeData: false,
    showMergeSuccessMessage: false,
    waitingForPerson: false,
    // data
    merge: {
      possibleDuplicate: {
        isSurvivingPerson: false,
        accessControl: null,
        lifesketch: {
          expandedHeight: 0,
          otherExpandedHeight: 0,
          showHiddenDetails: false,
          showMore: false,
          actionButtonType: null,
          displayStatus: itemDisplayStatus.REMOVE,
          value: null,
        },
        vitals: {
          name: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          sex: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          birth: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          christening: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          death: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          burial: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
        },
        otherInformation: [],
        spousesAndChildren: [],
        parents: [],
        associations: [],
        contactNames: null,
        introDetails: {
          name: null,
          gender: null,
          birthDate: null,
          deathDate: null,
          personId: null,
        },
      },
      survivingPerson: {
        isSurvivingPerson: true,
        accessControl: null,
        lifesketch: {
          expandedHeight: 0,
          otherExpandedHeight: 0,
          showHiddenDetails: false,
          showMore: false,
          actionButtonType: null,
          displayStatus: itemDisplayStatus.REMOVE,
          value: null,
        },
        vitals: {
          name: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          sex: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          birth: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          christening: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          death: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
          burial: {
            showHiddenDetails: false,
            actionButtonType: null,
            displayStatus: itemDisplayStatus.REMOVE,
            value: null,
          },
        },
        otherInformation: [],
        spousesAndChildren: [],
        parents: [],
        associations: [],
        contactNames: null,
        introDetails: {
          name: null,
          gender: null,
          birthDate: null,
          deathDate: null,
          personId: null,
        },
      },
    },
    mergedFieldIds: {
      idsOfConclusionsToCopy: [],
      idsOfConclusionsToDelete: [],
      idsOfCoupleRelationshipsToCopy: [],
      idsOfCoupleRelationshipsToDelete: [],
      idsOfParentChildRelationshipsToCopy: [],
      idsOfParentChildRelationshipsToDelete: [],
      idsOfSourceReferencesToCopy: [],
      attribution: {
        changeMessage: null,
      },
    },
    mergeConstraint: null,
    possibleDuplicate: null,
    possibleDuplicateId: null,
    possibleDuplicateInput: null,
    reasonForMerge: null,
    survivingPerson: null,
    survivingPersonId: null,
    survivingPersonInput: null,
  }
  // Reducer Hook
  const [adminMergeState, adminMergeDispatch] = useReducer(adminMergeReducer, initialState)
  // Effects
  useEffect(() => {
    let isCurrent = true
    if (adminMergeState.survivingPersonId && adminMergeState.possibleDuplicateId) {
      adminMergeDispatch({ type: actions.MERGE_SETUP })
      getMergeConstraints(adminMergeState.survivingPersonId, adminMergeState.possibleDuplicateId)
        .then((response) => {
          if (isCurrent) {
            if (response.data.personMergeConstraint !== 'CAN_MERGE_ANY_ORDER') {
              adminMergeDispatch({ type: actions.MERGE_CONSTRAINT, payload: response.data.personMergeConstraint })
            }
          }
        })
        .catch((err) => {
          error(`Error retrieving merge constraints: ${err.message || ''}`, err)
        })
    }
    return () => {
      isCurrent = false
    }
  }, [adminMergeState.possibleDuplicateId, adminMergeState.survivingPersonId, error])
  // Functions
  const actionButtonOnClick = (payload) => {
    adminMergeDispatch({ type: actions.ACTION_BUTTON_ON_CLICK, payload })
  }
  const continueBtnOnClick = () => {
    adminMergeDispatch({ type: actions.SHOW_MERGE_DATA, payload: true })
  }
  const getErrorMessage = (personId, person) => {
    if (adminMergeState.waitingForPerson) {
      return null
    }
    if (!personId) {
      return null
    }
    if (!isAlphaNumericPersonId(personId)) {
      return t('tools.adminMerge.notifications.invalidPersonId', 'Invalid Person ID')
    }
    if (!person) {
      return t('tools.adminMerge.notifications.personNotFound', 'Person Not Found')
    }
    return null
  }
  const getValidMessage = (personId, person) => {
    if (isAlphaNumericPersonId(personId)) {
      if (person) {
        return person.name
      }
      return t('tools.adminMerge.notifications.validPersonId', 'Valid Person ID')
    }
    return null
  }
  const setMetadata = (oldMetadata) => {
    const items = []
    if (oldMetadata.lifeSpan) {
      items.push({ ...oldMetadata.lifeSpan, label: t('lifespan', 'Lifespan'), showLabel: false, field: 'lifeSpan' })
    }
    if (oldMetadata.ids) {
      items.push({ ...oldMetadata.ids, label: t('id', 'ID'), showLabel: false, field: 'ids' })
    }
    if (oldMetadata.cisId) {
      items.push({ ...oldMetadata.cisId, label: t('cisId', 'CIS ID'), showLabel: true, field: 'cisId' })
    }
    if (oldMetadata.contributorId) {
      items.push({
        ...oldMetadata.contributorId,
        label: t('contributorId', 'Contributor ID'),
        showLabel: true,
        field: 'contributorId',
      })
    }
    if (oldMetadata.mrn) {
      items.push({ ...oldMetadata.mrn, label: t('mrn', 'MRN'), showLabel: true, field: 'mrn' })
    }

    const badges = []
    if (oldMetadata.tombstonedBadge) {
      badges.push({ ...oldMetadata.tombstonedBadge, field: 'tombstoned' })
    }
    if (oldMetadata.ownerBadge) {
      badges.push({ ...oldMetadata.ownerBadge, field: 'ownerCisId' })
    }
    if (oldMetadata.forwardsToBadge) {
      badges.push({ ...oldMetadata.forwardsToBadge, field: 'forwardsTo' })
    }
    if (oldMetadata.forwardedFromBadge) {
      badges.push({ ...oldMetadata.forwardedFromBadge, field: 'forwardedFromBadge' })
    }

    return { header: oldMetadata.header, items, badges }
  }
  const onPersonInputChange = async (input, isSurvivingPerson) => {
    adminMergeDispatch({ type: actions.SHOW_MERGE_DATA, payload: false })
    adminMergeDispatch({ type: actions.MERGE_CONSTRAINT, payload: null })
    const mergedFieldIdsReset = {
      idsOfConclusionsToCopy: [],
      idsOfConclusionsToDelete: [],
      idsOfCoupleRelationshipsToCopy: [],
      idsOfCoupleRelationshipsToDelete: [],
      idsOfParentChildRelationshipsToCopy: [],
      idsOfParentChildRelationshipsToDelete: [],
      idsOfAssociationRelationshipsToCopy: [],
      idsOfAssociationRelationshipsToDelete: [],
      idsOfSourceReferencesToCopy: [],
      attribution: {
        changeMessage: null,
      },
    }
    adminMergeDispatch({ type: actions.MERGED_FIELD_IDS, payload: mergedFieldIdsReset })
    adminMergeDispatch({ type: actions.SHOW_MERGE_SUCCESS_MESSAGE, payload: false })
    if (isSurvivingPerson) {
      adminMergeDispatch({ type: actions.SURVIVING_PERSON, payload: null })
      adminMergeDispatch({ type: actions.SURVIVING_PERSON_INPUT, payload: input })
    } else {
      adminMergeDispatch({ type: actions.POSSIBLE_DUPLICATE, payload: null })
      adminMergeDispatch({ type: actions.POSSIBLE_DUPLICATE_INPUT, payload: input })
    }
    if (isAlphaNumericPersonId(input)) {
      try {
        adminMergeDispatch({ type: actions.WAITING_FOR_PERSON, payload: true })
        const person = await getPerson(input.toUpperCase())
        person.metadata = setMetadata(person.metadata)
        const personWithMissingPersonOwnershipInfoWithoutMetaData = await getMissingRelationshipOwnerInfo(person)
        const personWithMissingPersonOwnershipInfoWithMetaData = {
          ...personWithMissingPersonOwnershipInfoWithoutMetaData.data,
          metadata: person.metadata,
        }
        adminMergeDispatch({ type: actions.WAITING_FOR_PERSON, payload: false })
        if (personWithMissingPersonOwnershipInfoWithMetaData) {
          if (isSurvivingPerson) {
            adminMergeDispatch({
              type: actions.SURVIVING_PERSON,
              payload: personWithMissingPersonOwnershipInfoWithMetaData,
            })
          } else {
            adminMergeDispatch({
              type: actions.POSSIBLE_DUPLICATE,
              payload: personWithMissingPersonOwnershipInfoWithMetaData,
            })
          }
        }
      } catch (err) {
        error(`Error retrieving person data: ${err.message || ''}`, err)
      }
      adminMergeDispatch({ type: actions.WAITING_FOR_PERSON, payload: false })
    }
  }
  const toggleShowMoreLessLifeSketch = () => {
    adminMergeDispatch({ type: actions.TOGGLE_SHOW_MORE_LESS_LIFE_SKETCH })
  }
  const toggleShowItemHiddenDetails = (payload) => {
    adminMergeDispatch({ type: actions.TOGGLE_SHOW_ITEM_HIDDEN_DETAILS, payload })
  }
  const setLifeSketchExpandedHeight = useCallback((height, isSurvivingPerson) => {
    adminMergeDispatch({ type: actions.LIFE_SKETCH_EXPANDED_HEIGHT, payload: { height, isSurvivingPerson } })
  }, [])
  const setChangeMessage = (changeMessage) => {
    adminMergeDispatch({ type: actions.CHANGE_MESSAGE, payload: changeMessage })
  }
  const finishMergeBtnOnClick = async () => {
    try {
      addAllSourceReferences(adminMergeState.possibleDuplicate.sourceReferences, adminMergeState.mergedFieldIds)
      await mergePersons(
        adminMergeState.possibleDuplicate.id,
        adminMergeState.survivingPerson.id,
        adminMergeState.mergedFieldIds
      )
      ok(t('tools.adminMerge.notifications.mergeComplete', 'Merge complete'))
      adminMergeDispatch({ type: actions.SHOW_MERGE_SUCCESS_MESSAGE, payload: true })
      adminMergeDispatch({ type: actions.SHOW_MERGE_DATA, payload: false })
    } catch (err) {
      const regex = /Warning:\s(\d{3})\s+tf\s+"(.*?)"/
      const match = err.response?.data?.notifications?.[0]?.msg?.match(regex)
      if (match) {
        error(`${t(`tools.adminMerge.notifications.mergeFail.${match[1]}`, 'Unable to perform this merge.')}`, err)
      } else {
        error(`${t('tools.adminMerge.notifications.mergeFail', 'Unable to perform this merge.')} ${err.message}`, err)
      }
    }
  }
  // Variables
  const continueBtnDisabled =
    !adminMergeState.possibleDuplicateId ||
    !adminMergeState.survivingPersonId ||
    !isAlphaNumericPersonId(adminMergeState.possibleDuplicateId) ||
    !isAlphaNumericPersonId(adminMergeState.survivingPersonId) ||
    adminMergeState.showMergeSuccessMessage
  // Helpers
  const helpers = {
    // functions
    actionButtonOnClick,
    continueBtnOnClick,
    getErrorMessage,
    getValidMessage,
    finishMergeBtnOnClick,
    onPersonInputChange,
    setLifeSketchExpandedHeight,
    setChangeMessage,
    toggleShowItemHiddenDetails,
    toggleShowMoreLessLifeSketch,
    // variables
    continueBtnDisabled,
  }
  return [adminMergeState, helpers]
}

function mergeSetupRelationships(state, possibleDuplicate, survivingPerson) {
  // spouse and children
  const possibleDuplicateSpousesAndChildren = getSpousesAndChildren(possibleDuplicate, true)
  const survivingPersonSpousesAndChildren = getSpousesAndChildren(survivingPerson)
  const { possibleDuplicateSpousesAndChildrenFinal, survivingPersonSpousesAndChildrenFinal } = initSpousesAndChildren(
    possibleDuplicateSpousesAndChildren,
    survivingPersonSpousesAndChildren,
    state.mergedFieldIds
  )
  state.merge.possibleDuplicate.spousesAndChildren = possibleDuplicateSpousesAndChildrenFinal
  state.merge.survivingPerson.spousesAndChildren = survivingPersonSpousesAndChildrenFinal
  // parents
  const possibleDuplicateParents = possibleDuplicate.relationships.parents
    ? possibleDuplicate.relationships.parents
    : []
  const survivingPersonParents = survivingPerson.relationships.parents ? survivingPerson.relationships.parents : []
  const { possibleDuplicateParentsFinal, survivingPersonParentsFinal } = initParents(
    possibleDuplicateParents,
    survivingPersonParents,
    state.mergedFieldIds
  )
  state.merge.possibleDuplicate.parents = possibleDuplicateParentsFinal
  state.merge.survivingPerson.parents = survivingPersonParentsFinal
  // associations
  const possibleDuplicateAssociations = possibleDuplicate.relationships.associations
    ? possibleDuplicate.relationships.associations
    : []
  const survivingPersonAssociations = survivingPerson.relationships.associations
    ? survivingPerson.relationships.associations
    : []
  const { possibleDuplicateAssociationsFinal, survivingPersonAssociationsFinal } = initAssociations(
    possibleDuplicateAssociations,
    survivingPersonAssociations,
    state.mergedFieldIds
  )
  state.merge.possibleDuplicate.associations = possibleDuplicateAssociationsFinal
  state.merge.survivingPerson.associations = survivingPersonAssociationsFinal
}

function mergeSetup(state, possibleDuplicate, survivingPerson) {
  // accessControl
  state.merge.possibleDuplicate.accessControl = possibleDuplicate.accessControl
  state.merge.survivingPerson.accessControl = survivingPerson.accessControl
  // life sketch
  state.merge.possibleDuplicate.lifesketch.value = getFactConclusion(possibleDuplicate, 'LifeSketch')
  state.merge.survivingPerson.lifesketch.value = getFactConclusion(survivingPerson, 'LifeSketch')
  itemInit(state.merge.possibleDuplicate.lifesketch, state.merge.survivingPerson.lifesketch, state.mergedFieldIds)
  // vitals
  // name
  state.merge.possibleDuplicate.vitals.name.value = possibleDuplicate.nameConclusion
  state.merge.survivingPerson.vitals.name.value = survivingPerson.nameConclusion
  itemInit(state.merge.possibleDuplicate.vitals.name, state.merge.survivingPerson.vitals.name, state.mergedFieldIds)
  // sex
  state.merge.possibleDuplicate.vitals.sex.value = possibleDuplicate.genderConclusion
  state.merge.survivingPerson.vitals.sex.value = survivingPerson.genderConclusion
  itemInit(state.merge.possibleDuplicate.vitals.sex, state.merge.survivingPerson.vitals.sex, state.mergedFieldIds)
  // birth
  state.merge.possibleDuplicate.vitals.birth.value = getFactConclusion(possibleDuplicate, 'Birth')
  state.merge.survivingPerson.vitals.birth.value = getFactConclusion(survivingPerson, 'Birth')
  itemInit(state.merge.possibleDuplicate.vitals.birth, state.merge.survivingPerson.vitals.birth, state.mergedFieldIds)
  // christening
  state.merge.possibleDuplicate.vitals.christening.value = getFactConclusion(possibleDuplicate, 'Christening')
  state.merge.survivingPerson.vitals.christening.value = getFactConclusion(survivingPerson, 'Christening')
  itemInit(
    state.merge.possibleDuplicate.vitals.christening,
    state.merge.survivingPerson.vitals.christening,
    state.mergedFieldIds
  )
  // death
  state.merge.possibleDuplicate.vitals.death.value = getFactConclusion(possibleDuplicate, 'Death')
  state.merge.survivingPerson.vitals.death.value = getFactConclusion(survivingPerson, 'Death')
  itemInit(state.merge.possibleDuplicate.vitals.death, state.merge.survivingPerson.vitals.death, state.mergedFieldIds)
  // burial
  state.merge.possibleDuplicate.vitals.burial.value = getFactConclusion(possibleDuplicate, 'Burial')
  state.merge.survivingPerson.vitals.burial.value = getFactConclusion(survivingPerson, 'Burial')
  itemInit(state.merge.possibleDuplicate.vitals.burial, state.merge.survivingPerson.vitals.burial, state.mergedFieldIds)
  // other information
  otherInformationInit(state, possibleDuplicate, survivingPerson)
  // relationships
  mergeSetupRelationships(state, possibleDuplicate, survivingPerson)
  // contact names
  state.merge.possibleDuplicate.contactNames = possibleDuplicate.contactNames
  state.merge.survivingPerson.contactNames = survivingPerson.contactNames
  // intro details
  state.merge.possibleDuplicate.introDetails = {
    name: possibleDuplicate.name,
    gender: possibleDuplicate?.gender?.toLowerCase(),
    lifespan: possibleDuplicate.lifespan,
    birthDate: possibleDuplicate?.lifespanBegin?.date?.normalized?.[0]?.value,
    deathDate: possibleDuplicate?.lifespanEnd?.date?.normalized?.[0]?.value,
    personId: possibleDuplicate.id,
  }
  state.merge.survivingPerson.introDetails = {
    name: survivingPerson.name,
    gender: survivingPerson?.gender?.toLowerCase(),
    lifespan: survivingPerson.lifespan,
    birthDate: survivingPerson?.lifespanBegin?.date?.normalized?.[0]?.value,
    deathDate: survivingPerson?.lifespanEnd?.date?.normalized?.[0]?.value,
    personId: survivingPerson.id,
  }
}

function getFactConclusion(person, type) {
  if (!person.factConclusions) {
    return null
  }
  return person.factConclusions.find((fact) => {
    return getLastPathElement(fact.value.type) === type
  })
}

function isNonVitalFactConclusion(factConclusion) {
  const VITAL_FACTS_TYPES = ['LifeSketch', 'Birth', 'Death', 'Christening', 'Burial']
  return !VITAL_FACTS_TYPES.includes(getLastPathElement(factConclusion?.value?.type))
}

function isDuplicateNonVitalFactConclusion(factConclusion, survivingPersonFactConclusions) {
  return survivingPersonFactConclusions.some((survivingPersonFactConclusion) => {
    if (
      (factConclusion?.value?.type === 'USER_DEFINED' &&
        factConclusion?.value?.userDefinedType === survivingPersonFactConclusion?.value?.userDefinedType) ||
      (factConclusion?.value?.type !== 'USER_DEFINED' &&
        factConclusion?.value?.type === survivingPersonFactConclusion?.value?.type)
    ) {
      const duplicatePersonFactConclusionValueStringified = JSON.stringify(factConclusion.value)
      const survivingPersonFactConclusionValueStringified = JSON.stringify(survivingPersonFactConclusion.value)
      if (duplicatePersonFactConclusionValueStringified === survivingPersonFactConclusionValueStringified) {
        return true
      }
      const duplicatePersonFactDateStringified = JSON.stringify(factConclusion?.value?.date)
      const survivingPersonFactDateStringified = JSON.stringify(survivingPersonFactConclusion?.value?.date)
      const duplicatePersonFactPlaceOriginalStringified = JSON.stringify(factConclusion?.value?.place?.original)
      const survivingPersonFactPlaceOriginalStringified = JSON.stringify(
        survivingPersonFactConclusion?.value?.place?.original
      )
      if (
        duplicatePersonFactDateStringified === survivingPersonFactDateStringified &&
        duplicatePersonFactPlaceOriginalStringified === survivingPersonFactPlaceOriginalStringified
      ) {
        return true
      }
    }
    return false
  })
}

function isOverwritableFact(factConclusion) {
  const OVERWRITABLE_FACTS = [
    'Cremation',
    'BarMitzvah',
    'BatMitzvah',
    'PhysicalDescription',
    'Stillbirth',
    'Nationality',
  ]
  return OVERWRITABLE_FACTS.includes(getLastPathElement(factConclusion?.value?.type))
}

function itemInit(possibleDuplicateFact, survivingPersonFact, mergedFieldIds) {
  if (possibleDuplicateFact.value && !survivingPersonFact.value) {
    possibleDuplicateFact.displayStatus = itemDisplayStatus.HIDDEN
    possibleDuplicateFact.actionButtonType = actionButtonTypes.HIDDEN
    survivingPersonFact.value = possibleDuplicateFact.value
    survivingPersonFact.displayStatus = itemDisplayStatus.SHOW
    survivingPersonFact.actionButtonType = actionButtonTypes.UNDO
    mergedFieldIds.idsOfConclusionsToCopy.push(possibleDuplicateFact.value.conclusionId)
  } else if (!possibleDuplicateFact.value && survivingPersonFact.value) {
    possibleDuplicateFact.value = survivingPersonFact.value
    possibleDuplicateFact.displayStatus = itemDisplayStatus.HIDDEN
    possibleDuplicateFact.actionButtonType = actionButtonTypes.NONE
    survivingPersonFact.displayStatus = itemDisplayStatus.SHOW
    survivingPersonFact.actionButtonType = actionButtonTypes.NONE
  } else if (possibleDuplicateFact.value && survivingPersonFact.value) {
    possibleDuplicateFact.actionButtonType = actionButtonTypes.REPLACE
    possibleDuplicateFact.displayStatus = itemDisplayStatus.SHOW
    survivingPersonFact.actionButtonType = actionButtonTypes.HIDDEN
    survivingPersonFact.displayStatus = itemDisplayStatus.SHOW
  } else {
    survivingPersonFact.actionButtonType = actionButtonTypes.NONE
    possibleDuplicateFact.actionButtonType = actionButtonTypes.NONE
    survivingPersonFact.displayStatus = itemDisplayStatus.REMOVE
    possibleDuplicateFact.displayStatus = itemDisplayStatus.REMOVE
  }
}

function otherInformationInit(state, possibleDuplicate, survivingPerson) {
  const possibleDuplicateOtherInformation = possibleDuplicate.factConclusions
    ? possibleDuplicate.factConclusions
        .filter((factConclusion) => isNonVitalFactConclusion(factConclusion))
        .filter(
          (factConclusion) =>
            !isDuplicateNonVitalFactConclusion(
              factConclusion,
              survivingPerson.factConclusions.filter((survingPersonFactConclusion) =>
                isNonVitalFactConclusion(survingPersonFactConclusion)
              )
            )
        )
        .filter((factConclusion) => !isOverwritableFact(factConclusion))
        .map((factConclusion) => {
          state.mergedFieldIds.idsOfConclusionsToCopy.push(factConclusion.conclusionId)
          return {
            showHiddenDetails: false,
            actionButtonType: actionButtonTypes.UNDO,
            value: factConclusion,
          }
        })
    : []
  const survivingPersonOtherInformation = survivingPerson.factConclusions
    ? survivingPerson.factConclusions
        .filter((factConclusion) => isNonVitalFactConclusion(factConclusion))
        .filter((factConclusion) => !isOverwritableFact(factConclusion))
        .map((factConclusion) => {
          return {
            showHiddenDetails: false,
            actionButtonType: actionButtonTypes.NONE,
            value: factConclusion,
          }
        })
    : []
  const allOtherInformation = survivingPersonOtherInformation.concat(possibleDuplicateOtherInformation)
  state.merge.possibleDuplicate.otherInformation = allOtherInformation.map((item) => {
    return {
      ...item,
      displayStatus: itemDisplayStatus.HIDDEN,
    }
  })
  state.merge.survivingPerson.otherInformation = allOtherInformation.map((item) => {
    return {
      ...item,
      displayStatus: itemDisplayStatus.SHOW,
    }
  })
  const possibleDuplicateOverwritableFacts = possibleDuplicate.factConclusions
    ? possibleDuplicate.factConclusions.filter((factConclusion) => isOverwritableFact(factConclusion))
    : []
  let survivingPersonOverwritableFacts = survivingPerson.factConclusions
    ? survivingPerson.factConclusions.filter((factConclusion) => isOverwritableFact(factConclusion))
    : []
  possibleDuplicateOverwritableFacts.forEach((possibleDuplicateOverwritableFact) => {
    const overwritableDuplicateFact = survivingPersonOverwritableFacts.find(
      (survivingPersonOverwritableFact) =>
        survivingPersonOverwritableFact.value.type === possibleDuplicateOverwritableFact.value.type
    )
    if (overwritableDuplicateFact) {
      state.merge.possibleDuplicate.otherInformation.push({
        showHiddenDetails: false,
        actionButtonType: actionButtonTypes.REPLACE,
        value: possibleDuplicateOverwritableFact,
        displayStatus: itemDisplayStatus.SHOW,
      })
      state.merge.survivingPerson.otherInformation.push({
        showHiddenDetails: false,
        actionButtonType: actionButtonTypes.HIDDEN,
        value: overwritableDuplicateFact,
        displayStatus: itemDisplayStatus.SHOW,
      })
      survivingPersonOverwritableFacts = survivingPersonOverwritableFacts.filter(
        (survivingPersonOverwritableFact) =>
          survivingPersonOverwritableFact.value.type !== overwritableDuplicateFact.value.type
      )
    } else {
      state.merge.possibleDuplicate.otherInformation.push({
        showHiddenDetails: false,
        actionButtonType: actionButtonTypes.HIDDEN,
        value: possibleDuplicateOverwritableFact,
        displayStatus: itemDisplayStatus.HIDDEN,
      })
      state.merge.survivingPerson.otherInformation.push({
        showHiddenDetails: false,
        actionButtonType: actionButtonTypes.UNDO,
        value: possibleDuplicateOverwritableFact,
        displayStatus: itemDisplayStatus.SHOW,
      })
    }
  })
  survivingPersonOverwritableFacts.forEach((survivingPersonOverwritableFact) => {
    state.merge.possibleDuplicate.otherInformation.push({
      showHiddenDetails: false,
      actionButtonType: actionButtonTypes.NONE,
      value: survivingPersonOverwritableFact,
      displayStatus: itemDisplayStatus.HIDDEN,
    })
    state.merge.survivingPerson.otherInformation.push({
      showHiddenDetails: false,
      actionButtonType: actionButtonTypes.NONE,
      value: survivingPersonOverwritableFact,
      displayStatus: itemDisplayStatus.SHOW,
    })
  })
}

function getSpousesAndChildren(person, isPossibleDuplicatePerson) {
  const spousesWithChildren = person.relationships.children
    ? person.relationships.children
        .filter((spouseAndChildren) => isPossibleDuplicatePerson || spouseAndChildren.parent.personId)
        .map((spouseAndChildren) => {
          return {
            spouse: {
              relationshipId: getSpouseRelationshipId(person.relationships.spouses, spouseAndChildren.parent.personId),
              value: spouseAndChildren.parent,
            },
            children: spouseAndChildren.children.map((child) => {
              return {
                value: child,
              }
            }),
          }
        })
    : []
  const spousesWithChildrenIds = person.relationships.children
    ? person.relationships.children.map((children) => children.parent.personId)
    : []
  const spousesWithoutChildren = person.relationships.spouses
    ? person.relationships.spouses
        .filter((spouse) => !spousesWithChildrenIds.includes(spouse.spouse.personId))
        .map((spouse) => {
          return {
            spouse: {
              relationshipId: spouse.relationshipId,
              value: spouse.spouse,
            },
            children: [],
          }
        })
    : []
  return spousesWithChildren.concat(spousesWithoutChildren)
}

function getSpouseRelationshipId(spouses, spousePersonId) {
  const spouse = spouses ? spouses.find((s) => spousePersonId === s.spouse.personId) : null
  return spouse?.relationshipId
}

function initSpousesAndChildren(
  possibleDuplicateSpousesAndChildren,
  survivingPersonSpousesAndChildren,
  mergedFieldIds
) {
  const possibleDuplicateSpousesAndChildrenFinal = []
  const survivingPersonSpousesAndChildrenFinal = []
  possibleDuplicateSpousesAndChildren.forEach((possibleDuplicateSpouseAndChildren) => {
    const duplicateSpouse = survivingPersonSpousesAndChildren.find(
      (survivingPersonSpouseAndChildren) =>
        possibleDuplicateSpouseAndChildren.spouse.value.personId ===
        survivingPersonSpouseAndChildren.spouse.value.personId
    )
    if (duplicateSpouse) {
      const possibleDuplicateChildren = []
      const survivingPersonChildren = []
      possibleDuplicateSpouseAndChildren.children.forEach((possibleDuplicateChild) => {
        const duplicateChild = duplicateSpouse.children.find((child) => {
          return possibleDuplicateChild.value.child.personId === child.value.child.personId
        })
        if (duplicateChild) {
          possibleDuplicateChildren.push({
            ...possibleDuplicateChild,
            childType: 'duplicate',
          })
          survivingPersonChildren.push({
            ...duplicateChild,
            childType: 'duplicate',
          })
          duplicateSpouse.children = duplicateSpouse.children.filter((child) => {
            return duplicateChild.value.child.personId !== child.value.child.personId
          })
        } else {
          possibleDuplicateChildren.push({
            ...possibleDuplicateChild,
            childType: 'possibleDuplicateChild',
          })
          survivingPersonChildren.push({
            ...possibleDuplicateChild,
            childType: 'possibleDuplicateChild',
          })
          mergedFieldIds.idsOfParentChildRelationshipsToCopy.push(possibleDuplicateChild.value.relationshipId)
        }
      })
      duplicateSpouse.children.forEach((child) => {
        possibleDuplicateChildren.push({
          ...child,
          childType: 'survivingPersonChild',
        })
        survivingPersonChildren.push({
          ...child,
          childType: 'survivingPersonChild',
        })
      })
      possibleDuplicateSpousesAndChildrenFinal.push({
        spouse: {
          ...possibleDuplicateSpouseAndChildren.spouse,
          actionButtonType: actionButtonTypes.REPLACE,
          displayStatus: itemDisplayStatus.SHOW,
        },
        children: possibleDuplicateChildren
          .filter((child) => !child.value.child.ownerInfo?.ownerCisId && !child.value.child.ownerInfo?.ownerGroupId)
          .map((child) => {
            return {
              ...child,
              displayStatus: getDisplayStatusForChild(child.childType, 'possibleDuplicate'),
              actionButtonType: getActionButtonTypeForChild(child.childType, 'possibleDuplicate'),
              value: child.childType === 'possibleDuplicateChild' ? null : child.value,
            }
          }),
      })
      survivingPersonSpousesAndChildrenFinal.push({
        spouse: {
          ...duplicateSpouse.spouse,
          actionButtonType: actionButtonTypes.HIDDEN,
          displayStatus: itemDisplayStatus.SHOW,
        },
        children: survivingPersonChildren
          .filter((child) => !child.value.child.ownerInfo?.ownerCisId && !child.value.child.ownerInfo?.ownerGroupId)
          .map((child) => {
            return {
              ...child,
              displayStatus: getDisplayStatusForChild(child.childType, 'survivingPerson'),
              actionButtonType: getActionButtonTypeForChild(child.childType, 'survivingPerson'),
            }
          }),
      })
      survivingPersonSpousesAndChildren = survivingPersonSpousesAndChildren.filter(
        (survivingPersonSpouseAndChildren) =>
          duplicateSpouse.spouse.value.personId !== survivingPersonSpouseAndChildren.spouse.value.personId
      )
    } else {
      possibleDuplicateSpousesAndChildrenFinal.push({
        spouse: {
          ...possibleDuplicateSpouseAndChildren.spouse,
          value: null,
          actionButtonType: possibleDuplicateSpouseAndChildren.spouse.value.personId
            ? actionButtonTypes.HIDDEN
            : actionButtonTypes.NONE,
          displayStatus: itemDisplayStatus.HIDDEN,
          noSpouse: !possibleDuplicateSpouseAndChildren.spouse.value.personId,
        },
        children: possibleDuplicateSpouseAndChildren.children.map((child) => {
          return {
            ...child,
            value: null,
            actionButtonType: actionButtonTypes.HIDDEN,
            displayStatus: itemDisplayStatus.HIDDEN,
          }
        }),
      })
      survivingPersonSpousesAndChildrenFinal.push({
        spouse: {
          ...possibleDuplicateSpouseAndChildren.spouse,
          actionButtonType: possibleDuplicateSpouseAndChildren.spouse.value.personId
            ? actionButtonTypes.UNDO
            : actionButtonTypes.NONE,
          displayStatus: itemDisplayStatus.SHOW,
          noSpouse: !possibleDuplicateSpouseAndChildren.spouse.value.personId,
        },
        children: possibleDuplicateSpouseAndChildren.children.map((child) => {
          return {
            ...child,
            actionButtonType: actionButtonTypes.UNDO,
            displayStatus: itemDisplayStatus.SHOW,
          }
        }),
      })
      if (possibleDuplicateSpouseAndChildren.spouse.relationshipId) {
        mergedFieldIds.idsOfCoupleRelationshipsToCopy.push(possibleDuplicateSpouseAndChildren.spouse.relationshipId)
      }
      possibleDuplicateSpouseAndChildren.children.forEach((child) => {
        mergedFieldIds.idsOfParentChildRelationshipsToCopy.push(child.value.relationshipId)
      })
    }
  })
  survivingPersonSpousesAndChildren.forEach((survivingPersonSpouseAndChildren) => {
    if (
      !survivingPersonSpouseAndChildren.spouse.value.ownerInfo?.ownerCisId &&
      !survivingPersonSpouseAndChildren.spouse.value.ownerInfo?.ownerGroupId
    ) {
      possibleDuplicateSpousesAndChildrenFinal.push({
        spouse: {
          ...survivingPersonSpouseAndChildren.spouse,
          value: null,
          actionButtonType: actionButtonTypes.NONE,
          displayStatus: itemDisplayStatus.HIDDEN,
        },
        children: survivingPersonSpouseAndChildren.children
          .filter((child) => !child.value.child.ownerInfo?.ownerCisId && !child.value.child.ownerInfo?.ownerGroupId)
          .map((child) => {
            return {
              ...child,
              value: null,
              actionButtonType: actionButtonTypes.NONE,
              displayStatus: itemDisplayStatus.HIDDEN,
            }
          }),
      })
      survivingPersonSpousesAndChildrenFinal.push({
        spouse: {
          ...survivingPersonSpouseAndChildren.spouse,
          actionButtonType: actionButtonTypes.NONE,
          displayStatus: itemDisplayStatus.SHOW,
        },
        children: survivingPersonSpouseAndChildren.children
          .filter((child) => !child.value.child.ownerInfo?.ownerCisId && !child.value.child.ownerInfo?.ownerGroupId)
          .map((child) => {
            return {
              ...child,
              actionButtonType: actionButtonTypes.NONE,
              displayStatus: itemDisplayStatus.SHOW,
            }
          }),
      })
    }
  })
  return { possibleDuplicateSpousesAndChildrenFinal, survivingPersonSpousesAndChildrenFinal }
}

function initParents(possibleDuplicateParents, survivingPersonParents, mergedFieldIds) {
  const possibleDuplicateParentsFinal = []
  const survivingPersonParentsFinal = []
  // Add all possibleDuplicateParents
  possibleDuplicateParents.forEach((possibleDuplicateParentPair) => {
    const duplicateParentPair = survivingPersonParents.find(
      (survivingPersonParentPair) =>
        possibleDuplicateParentPair.parent1.personId === survivingPersonParentPair.parent1.personId &&
        possibleDuplicateParentPair.parent2.personId === survivingPersonParentPair.parent2.personId
    )
    if (duplicateParentPair) {
      possibleDuplicateParentsFinal.push({
        value: duplicateParentPair,
        actionButtonType: actionButtonTypes.REPLACE,
        displayStatus: itemDisplayStatus.SHOW,
      })
      survivingPersonParentsFinal.push({
        value: duplicateParentPair,
        actionButtonType: actionButtonTypes.HIDDEN,
        displayStatus: itemDisplayStatus.SHOW,
      })
      survivingPersonParents = survivingPersonParents.filter((survivingPersonParentPair) => {
        return !(
          duplicateParentPair.parent1?.personId === survivingPersonParentPair.parent1?.personId &&
          duplicateParentPair.parent2?.personId === survivingPersonParentPair.parent2?.personId
        )
      })
    } else {
      possibleDuplicateParentsFinal.push({
        value: possibleDuplicateParentPair,
        actionButtonType: actionButtonTypes.HIDDEN,
        displayStatus: itemDisplayStatus.HIDDEN,
      })
      survivingPersonParentsFinal.push({
        value: possibleDuplicateParentPair,
        actionButtonType: actionButtonTypes.UNDO,
        displayStatus: itemDisplayStatus.SHOW,
      })
      mergedFieldIds.idsOfParentChildRelationshipsToCopy.push(possibleDuplicateParentPair.relationshipId)
    }
  })
  survivingPersonParents.forEach((survivingPersonParentPair) => {
    possibleDuplicateParentsFinal.push({
      value: survivingPersonParentPair,
      actionButtonType: actionButtonTypes.NONE,
      displayStatus: itemDisplayStatus.HIDDEN,
    })
    survivingPersonParentsFinal.push({
      value: survivingPersonParentPair,
      actionButtonType: actionButtonTypes.NONE,
      displayStatus: itemDisplayStatus.SHOW,
    })
  })
  // Add the left overs for surviving parents
  return { possibleDuplicateParentsFinal, survivingPersonParentsFinal }
}

function initAssociations(possibleDuplicateAssociations, survivingPersonAssociations, mergedFieldIds) {
  const possibleDuplicateAssociationsFinal = []
  const survivingPersonAssociationsFinal = []

  possibleDuplicateAssociations.forEach((possibleDuplicateAssociation) => {
    const duplicateAssociation = survivingPersonAssociations.find(
      (survivingPersonAssociation) =>
        possibleDuplicateAssociation.person1.personId === survivingPersonAssociation.person1.personId ||
        possibleDuplicateAssociation.person2.personId === survivingPersonAssociation.person2.personId
    )
    if (duplicateAssociation) {
      possibleDuplicateAssociationsFinal.push({
        value: possibleDuplicateAssociation,
        actionButtonType: actionButtonTypes.REPLACE,
        displayStatus: itemDisplayStatus.SHOW,
      })

      survivingPersonAssociationsFinal.push({
        value: duplicateAssociation,
        actionButtonType: actionButtonTypes.HIDDEN,
        displayStatus: itemDisplayStatus.SHOW,
      })
      survivingPersonAssociations = survivingPersonAssociations.filter(
        (survivingPersonAssociation) =>
          duplicateAssociation.person1.personId !== survivingPersonAssociation.person1.personId ||
          duplicateAssociation.person2.personId !== survivingPersonAssociation.person2.personId
      )
    } else {
      possibleDuplicateAssociationsFinal.push({
        duplicate: false,
        value: possibleDuplicateAssociation,
        actionButtonType: actionButtonTypes.HIDDEN,
        displayStatus: itemDisplayStatus.HIDDEN,
      })
      survivingPersonAssociationsFinal.push({
        value: possibleDuplicateAssociation,
        actionButtonType: actionButtonTypes.UNDO,
        displayStatus: itemDisplayStatus.SHOW,
      })
      mergedFieldIds.idsOfAssociationRelationshipsToCopy.push(possibleDuplicateAssociation.relationshipId)
    }
  })

  survivingPersonAssociations.forEach((survivingPersonAssociation) => {
    possibleDuplicateAssociationsFinal.push({
      value: survivingPersonAssociation,
      actionButtonType: actionButtonTypes.NONE,
      displayStatus: itemDisplayStatus.HIDDEN,
    })
    survivingPersonAssociationsFinal.push({
      value: survivingPersonAssociation,
      actionButtonType: actionButtonTypes.NONE,
      displayStatus: itemDisplayStatus.SHOW,
    })
  })

  return { possibleDuplicateAssociationsFinal, survivingPersonAssociationsFinal }
}

function getDisplayStatusForChild(childType, personDataType) {
  let displayStatus
  switch (childType) {
    case 'duplicate': {
      displayStatus = itemDisplayStatus.SHOW
      break
    }
    case 'possibleDuplicateChild': {
      displayStatus = personDataType === 'possibleDuplicate' ? itemDisplayStatus.HIDDEN : itemDisplayStatus.SHOW
      break
    }
    case 'survivingPersonChild': {
      displayStatus = personDataType === 'possibleDuplicate' ? itemDisplayStatus.HIDDEN : itemDisplayStatus.SHOW
      break
    }
    default:
      break
  }
  return displayStatus
}

function getActionButtonTypeForChild(childType, personDataType) {
  let actionButtonType
  switch (childType) {
    case 'duplicate': {
      actionButtonType = personDataType === 'possibleDuplicate' ? actionButtonTypes.REPLACE : actionButtonTypes.HIDDEN
      break
    }
    case 'possibleDuplicateChild': {
      actionButtonType = personDataType === 'possibleDuplicate' ? actionButtonTypes.HIDDEN : actionButtonTypes.UNDO
      break
    }
    case 'survivingPersonChild': {
      actionButtonType = actionButtonTypes.NONE
      break
    }
    default:
      break
  }
  return actionButtonType
}

function toggleShowItemHiddenDetailsHelper(state, payload) {
  const { item, index } = payload
  switch (item) {
    case 'LifeSketch': {
      state.merge.possibleDuplicate.lifesketch.showHiddenDetails =
        !state.merge.possibleDuplicate.lifesketch.showHiddenDetails
      state.merge.survivingPerson.lifesketch.showHiddenDetails =
        !state.merge.survivingPerson.lifesketch.showHiddenDetails
      break
    }
    case 'Birth':
    case 'Burial':
    case 'Christening':
    case 'Death':
    case 'Name':
    case 'Sex': {
      const itemField = item.toLowerCase()
      state.merge.possibleDuplicate.vitals[itemField].showHiddenDetails =
        !state.merge.possibleDuplicate.vitals[itemField].showHiddenDetails
      state.merge.survivingPerson.vitals[itemField].showHiddenDetails =
        !state.merge.survivingPerson.vitals[itemField].showHiddenDetails
      break
    }
    case 'Other': {
      state.merge.possibleDuplicate.otherInformation[index].showHiddenDetails =
        !state.merge.possibleDuplicate.otherInformation[index].showHiddenDetails
      state.merge.survivingPerson.otherInformation[index].showHiddenDetails =
        !state.merge.survivingPerson.otherInformation[index].showHiddenDetails
      break
    }
    default: {
      break
    }
  }
}

function getParentPair(person, parent1Id, parent2Id) {
  return person.relationships.parents?.find((parentPair) => {
    return parentPair.parent1.personId === parent1Id && parentPair.parent2.personId === parent2Id
  })
}

function actionButtonOnClickHelper(state, payload) {
  const { item, actionButtonType, index, childIndex } = payload
  switch (item) {
    case 'LifeSketch': {
      const possibleDuplicateLifeSketchFactConclusion = getFactConclusion(state.possibleDuplicate, item)
      const survivingPersonLifeSketchFactConclusion = getFactConclusion(state.survivingPerson, item)
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.lifesketch.actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.lifesketch.displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.lifesketch.value = possibleDuplicateLifeSketchFactConclusion
        state.merge.survivingPerson.lifesketch.expandedHeight = state.merge.possibleDuplicate.lifesketch.expandedHeight
        state.merge.possibleDuplicate.lifesketch.actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.lifesketch.displayStatus = itemDisplayStatus.HIDDEN
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.lifesketch.actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.lifesketch.value = survivingPersonLifeSketchFactConclusion
        if (!state.merge.survivingPerson.lifesketch.value) {
          state.merge.survivingPerson.lifesketch.value = possibleDuplicateLifeSketchFactConclusion
          state.merge.survivingPerson.lifesketch.displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.survivingPerson.lifesketch.expandedHeight =
          state.merge.possibleDuplicate.lifesketch.otherExpandedHeight
        state.merge.possibleDuplicate.lifesketch.actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.lifesketch.displayStatus = itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.lifesketch.value = possibleDuplicateLifeSketchFactConclusion
      }
      updateIdsOfConclusionsToCopyAndDelete(
        state,
        actionButtonType,
        possibleDuplicateLifeSketchFactConclusion,
        survivingPersonLifeSketchFactConclusion
      )
      break
    }
    case 'Birth':
    case 'Burial':
    case 'Christening':
    case 'Death': {
      const itemField = item.toLowerCase()
      const possibleDuplicateFactConclusion = getFactConclusion(state.possibleDuplicate, item)
      const survivingPersonFactConclusion = getFactConclusion(state.survivingPerson, item)
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.vitals[itemField].actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.vitals[itemField].displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.vitals[itemField].value = possibleDuplicateFactConclusion
        state.merge.possibleDuplicate.vitals[itemField].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.vitals[itemField].displayStatus = itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.vitals[itemField].value = survivingPersonFactConclusion
        if (!state.merge.possibleDuplicate.vitals[itemField].value) {
          state.merge.possibleDuplicate.vitals[itemField].value = possibleDuplicateFactConclusion
        }
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.vitals[itemField].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.vitals[itemField].value = survivingPersonFactConclusion
        if (!state.merge.survivingPerson.vitals[itemField].value) {
          state.merge.survivingPerson.vitals[itemField].value = possibleDuplicateFactConclusion
          state.merge.survivingPerson.vitals[itemField].displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.vitals[itemField].actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.vitals[itemField].displayStatus = itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.vitals[itemField].value = possibleDuplicateFactConclusion
      }
      updateIdsOfConclusionsToCopyAndDelete(
        state,
        actionButtonType,
        possibleDuplicateFactConclusion,
        survivingPersonFactConclusion
      )
      break
    }
    case 'Name': {
      const itemField = item.toLowerCase()
      const possibleDuplicateNameConclusion = state.possibleDuplicate.nameConclusion
      const survivingPersonNameConclusion = state.survivingPerson.nameConclusion
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.vitals[itemField].actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.vitals[itemField].displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.vitals[itemField].value = possibleDuplicateNameConclusion
        state.merge.possibleDuplicate.vitals[itemField].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.vitals[itemField].displayStatus = itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.vitals[itemField].value = survivingPersonNameConclusion
        if (!state.merge.possibleDuplicate.vitals[itemField].value) {
          state.merge.possibleDuplicate.vitals[itemField].value = possibleDuplicateNameConclusion
        }
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.vitals[itemField].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.vitals[itemField].value = survivingPersonNameConclusion
        if (!state.merge.survivingPerson.vitals[itemField].value) {
          state.merge.survivingPerson.vitals[itemField].value = possibleDuplicateNameConclusion
          state.merge.survivingPerson.vitals[itemField].displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.vitals[itemField].actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.vitals[itemField].displayStatus = itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.vitals[itemField].value = possibleDuplicateNameConclusion
      }
      updateIdsOfConclusionsToCopyAndDelete(
        state,
        actionButtonType,
        possibleDuplicateNameConclusion,
        survivingPersonNameConclusion
      )
      break
    }
    case 'Sex': {
      const itemField = item.toLowerCase()
      const possibleDuplicateGenderConclusion = state.possibleDuplicate.genderConclusion
      const survivingPersonGenderConclusion = state.survivingPerson.genderConclusion
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.vitals[itemField].actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.vitals[itemField].displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.vitals[itemField].value = possibleDuplicateGenderConclusion
        state.merge.possibleDuplicate.vitals[itemField].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.vitals[itemField].displayStatus = itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.vitals[itemField].value = survivingPersonGenderConclusion
        if (!state.merge.possibleDuplicate.vitals[itemField].value) {
          state.merge.possibleDuplicate.vitals[itemField].value = possibleDuplicateGenderConclusion
        }
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.vitals[itemField].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.vitals[itemField].value = possibleDuplicateGenderConclusion
        if (!state.merge.survivingPerson.vitals[itemField].value) {
          state.merge.survivingPerson.vitals[itemField].value = possibleDuplicateGenderConclusion
          state.merge.survivingPerson.vitals[itemField].displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.vitals[itemField].actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.vitals[itemField].value = survivingPersonGenderConclusion
        state.merge.possibleDuplicate.vitals[itemField].displayStatus = itemDisplayStatus.SHOW
      }
      updateIdsOfConclusionsToCopyAndDelete(
        state,
        actionButtonType,
        possibleDuplicateGenderConclusion,
        survivingPersonGenderConclusion
      )
      break
    }
    case 'Other': {
      const possibleDuplicateFactValue = state.merge.possibleDuplicate.otherInformation[index].value
      const survivingPersonFactValue = state.merge.survivingPerson.otherInformation[index].value
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.otherInformation[index].actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.otherInformation[index].displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.otherInformation[index].value = possibleDuplicateFactValue
        state.merge.possibleDuplicate.otherInformation[index].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.otherInformation[index].displayStatus = itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.otherInformation[index].value = survivingPersonFactValue
        updateIdsOfConclusionsToCopyAndDelete(
          state,
          actionButtonType,
          possibleDuplicateFactValue,
          survivingPersonFactValue
        )
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.otherInformation[index].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.otherInformation[index].value = possibleDuplicateFactValue
        if (possibleDuplicateFactValue.conclusionId === survivingPersonFactValue.conclusionId) {
          state.merge.survivingPerson.otherInformation[index].displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.otherInformation[index].actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.otherInformation[index].displayStatus = itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.otherInformation[index].value = survivingPersonFactValue
        updateIdsOfConclusionsToCopyAndDelete(
          state,
          actionButtonType,
          survivingPersonFactValue,
          possibleDuplicateFactValue
        )
      }

      break
    }
    case 'Spouse': {
      const possibleDuplicateSpouseValue = state.merge.possibleDuplicate.spousesAndChildren[index].spouse.value
      const survivingPersonSpouseValue = state.merge.survivingPerson.spousesAndChildren[index].spouse.value
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.spousesAndChildren[index].spouse.actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.spousesAndChildren[index].spouse.displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.spousesAndChildren[index].spouse.value = possibleDuplicateSpouseValue
        state.merge.possibleDuplicate.spousesAndChildren[index].spouse.actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.spousesAndChildren[index].spouse.displayStatus = itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.spousesAndChildren[index].spouse.value = survivingPersonSpouseValue
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.spousesAndChildren[index].spouse.actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.spousesAndChildren[index].spouse.value = possibleDuplicateSpouseValue
        if (!state.merge.survivingPerson.spousesAndChildren[index].spouse.value) {
          state.merge.survivingPerson.spousesAndChildren[index].spouse.displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.spousesAndChildren[index].spouse.actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.spousesAndChildren[index].spouse.displayStatus = itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.spousesAndChildren[index].spouse.value = survivingPersonSpouseValue
      }
      updateIdsOfCoupleRelationshipsToCopyAndDelete(
        state,
        actionButtonType,
        state.merge.possibleDuplicate.spousesAndChildren[index].spouse,
        state.merge.survivingPerson.spousesAndChildren[index].spouse
      )
      break
    }
    case 'Child': {
      const possibleDuplicateChildValue =
        state.merge.possibleDuplicate.spousesAndChildren[index].children[childIndex].value
      const survivingPersonChildValue = state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].value
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].actionButtonType =
          actionButtonTypes.UNDO
        state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].displayStatus =
          itemDisplayStatus.SHOW
        state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].value = possibleDuplicateChildValue
        state.merge.possibleDuplicate.spousesAndChildren[index].children[childIndex].actionButtonType =
          actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.spousesAndChildren[index].children[childIndex].displayStatus =
          itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.spousesAndChildren[index].children[childIndex].value = survivingPersonChildValue
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].actionButtonType =
          actionButtonTypes.HIDDEN
        state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].value = possibleDuplicateChildValue
        if (!state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].value) {
          state.merge.survivingPerson.spousesAndChildren[index].children[childIndex].displayStatus =
            itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.spousesAndChildren[index].children[childIndex].actionButtonType =
          actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.spousesAndChildren[index].children[childIndex].displayStatus =
          itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.spousesAndChildren[index].children[childIndex].value = survivingPersonChildValue
      }
      updateIdsOfParentChildRelationshipsToCopyAndDeleteForChild(
        state,
        actionButtonType,
        possibleDuplicateChildValue,
        survivingPersonChildValue
      )
      break
    }
    case 'Parents': {
      const possibleDuplicateParentsValue = getParentPair(
        state.possibleDuplicate,
        state.merge.possibleDuplicate.parents[index].value.parent1.personId,
        state.merge.possibleDuplicate.parents[index].value.parent2.personId
      )
      const survivingPersonParentsValue = getParentPair(
        state.survivingPerson,
        state.merge.survivingPerson.parents[index].value.parent1.personId,
        state.merge.survivingPerson.parents[index].value.parent2.personId
      )
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.parents[index].actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.parents[index].displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.parents[index].value = possibleDuplicateParentsValue
        state.merge.possibleDuplicate.parents[index].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.parents[index].displayStatus = itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.parents[index].value = survivingPersonParentsValue
        if (!state.merge.possibleDuplicate.parents[index].value) {
          state.merge.possibleDuplicate.parents[index].value = possibleDuplicateParentsValue
        }
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.parents[index].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.parents[index].value = survivingPersonParentsValue
        if (!state.merge.survivingPerson.parents[index].value) {
          state.merge.survivingPerson.parents[index].value = possibleDuplicateParentsValue
          state.merge.survivingPerson.parents[index].displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.parents[index].actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.parents[index].displayStatus = itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.parents[index].value = possibleDuplicateParentsValue
      }
      updateIdsOfParentChildRelationshipsToCopyAndDeleteForParents(
        state,
        actionButtonType,
        possibleDuplicateParentsValue,
        survivingPersonParentsValue
      )
      break
    }
    case 'Associations': {
      const possibleDuplicateAssociationValue = state.merge.possibleDuplicate.associations[index].value
      const survivingPersonAssociationValue = state.merge.survivingPerson.associations[index].value
      if (actionButtonType === actionButtonTypes.REPLACE) {
        state.merge.survivingPerson.associations[index].actionButtonType = actionButtonTypes.UNDO
        state.merge.survivingPerson.associations[index].displayStatus = itemDisplayStatus.SHOW
        state.merge.survivingPerson.associations[index].value = possibleDuplicateAssociationValue
        state.merge.possibleDuplicate.associations[index].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.possibleDuplicate.associations[index].displayStatus = itemDisplayStatus.HIDDEN
        state.merge.possibleDuplicate.associations[index].value = survivingPersonAssociationValue
      }
      if (actionButtonType === actionButtonTypes.UNDO) {
        state.merge.survivingPerson.associations[index].actionButtonType = actionButtonTypes.HIDDEN
        state.merge.survivingPerson.associations[index].value = possibleDuplicateAssociationValue
        if (state.merge.possibleDuplicate.associations[index].duplicate === false) {
          state.merge.survivingPerson.associations[index].displayStatus = itemDisplayStatus.HIDDEN
        }
        state.merge.possibleDuplicate.associations[index].actionButtonType = actionButtonTypes.REPLACE
        state.merge.possibleDuplicate.associations[index].displayStatus = itemDisplayStatus.SHOW
        state.merge.possibleDuplicate.associations[index].value = survivingPersonAssociationValue
      }
      updateIdsOfAssociationRelationshipsToCopyAndDelete(
        state,
        actionButtonType,
        state.merge.possibleDuplicate.associations[index].value,
        state.merge.survivingPerson.associations[index].value
      )
      break
    }
    default: {
      break
    }
  }
}

function updateIdsOfConclusionsToCopyAndDelete(
  state,
  actionButtonType,
  possibleDuplicateFactConclusion,
  survivingPersonFactConclusion
) {
  if (actionButtonType === actionButtonTypes.REPLACE) {
    state.mergedFieldIds.idsOfConclusionsToCopy.push(possibleDuplicateFactConclusion.conclusionId)
    if (
      survivingPersonFactConclusion &&
      survivingPersonFactConclusion.conclusionId !== possibleDuplicateFactConclusion.conclusionId
    ) {
      state.mergedFieldIds.idsOfConclusionsToDelete.push(survivingPersonFactConclusion.conclusionId)
    }
  } else if (actionButtonType === actionButtonTypes.UNDO) {
    state.mergedFieldIds.idsOfConclusionsToCopy = state.mergedFieldIds.idsOfConclusionsToCopy.filter(
      (id) => id !== possibleDuplicateFactConclusion.conclusionId
    )
    if (survivingPersonFactConclusion) {
      state.mergedFieldIds.idsOfConclusionsToDelete = state.mergedFieldIds.idsOfConclusionsToDelete.filter(
        (id) => id !== survivingPersonFactConclusion.conclusionId
      )
    }
  }
}

function updateIdsOfCoupleRelationshipsToCopyAndDelete(
  state,
  actionButtonType,
  possibleDuplicateSpouse,
  survivingPersonSpouse
) {
  if (actionButtonType === actionButtonTypes.REPLACE) {
    state.mergedFieldIds.idsOfCoupleRelationshipsToCopy.push(possibleDuplicateSpouse.relationshipId)
    if (survivingPersonSpouse.relationshipId !== possibleDuplicateSpouse.relationshipId) {
      state.mergedFieldIds.idsOfCoupleRelationshipsToDelete.push(survivingPersonSpouse.relationshipId)
    }
  } else if (actionButtonType === actionButtonTypes.UNDO) {
    state.mergedFieldIds.idsOfCoupleRelationshipsToCopy = state.mergedFieldIds.idsOfCoupleRelationshipsToCopy.filter(
      (id) => id !== possibleDuplicateSpouse.relationshipId
    )
    if (survivingPersonSpouse.relationshipId !== possibleDuplicateSpouse.relationshipId) {
      state.mergedFieldIds.idsOfCoupleRelationshipsToDelete =
        state.mergedFieldIds.idsOfCoupleRelationshipsToDelete.filter(
          (id) => id !== survivingPersonSpouse.relationshipId
        )
    }
  }
}

function updateIdsOfParentChildRelationshipsToCopyAndDeleteForParents(
  state,
  actionButtonType,
  possibleDuplicateValue,
  survivingPersonValue
) {
  if (actionButtonType === actionButtonTypes.REPLACE) {
    state.mergedFieldIds.idsOfParentChildRelationshipsToCopy.push(possibleDuplicateValue.relationshipId)
    if (survivingPersonValue && survivingPersonValue.relationshipId !== possibleDuplicateValue.relationshipId) {
      state.mergedFieldIds.idsOfParentChildRelationshipsToDelete.push(survivingPersonValue.relationshipId)
    }
  } else if (actionButtonType === actionButtonTypes.UNDO) {
    state.mergedFieldIds.idsOfParentChildRelationshipsToCopy =
      state.mergedFieldIds.idsOfParentChildRelationshipsToCopy.filter((id) => {
        return id !== possibleDuplicateValue.relationshipId
      })
    if (survivingPersonValue && survivingPersonValue.relationshipId !== possibleDuplicateValue.relationshipId) {
      state.mergedFieldIds.idsOfParentChildRelationshipsToDelete =
        state.mergedFieldIds.idsOfParentChildRelationshipsToDelete.filter(
          (id) => id !== survivingPersonValue.relationshipId
        )
    }
  }
}

function updateIdsOfParentChildRelationshipsToCopyAndDeleteForChild(
  state,
  actionButtonType,
  possibleDuplicateValue,
  survivingPersonValue
) {
  if (actionButtonType === actionButtonTypes.REPLACE) {
    state.mergedFieldIds.idsOfParentChildRelationshipsToCopy.push(possibleDuplicateValue.relationshipId)
    if (survivingPersonValue && survivingPersonValue.relationshipId !== possibleDuplicateValue.relationshipId) {
      state.mergedFieldIds.idsOfParentChildRelationshipsToDelete.push(survivingPersonValue.relationshipId)
    }
  } else if (actionButtonType === actionButtonTypes.UNDO) {
    state.mergedFieldIds.idsOfParentChildRelationshipsToCopy =
      state.mergedFieldIds.idsOfParentChildRelationshipsToCopy.filter((id) => {
        return id !== survivingPersonValue.relationshipId
      })
    if (possibleDuplicateValue && survivingPersonValue.relationshipId !== possibleDuplicateValue.relationshipId) {
      state.mergedFieldIds.idsOfParentChildRelationshipsToDelete =
        state.mergedFieldIds.idsOfParentChildRelationshipsToDelete.filter(
          (id) => id !== possibleDuplicateValue.relationshipId
        )
    }
  }
}

function updateIdsOfAssociationRelationshipsToCopyAndDelete(
  state,
  actionButtonType,
  possibleDuplicateAssociation,
  survivingPersonAssociation
) {
  if (actionButtonType === actionButtonTypes.REPLACE) {
    if (
      !state.mergedFieldIds.idsOfAssociationRelationshipsToCopy.includes(possibleDuplicateAssociation.relationshipId)
    ) {
      state.mergedFieldIds.idsOfAssociationRelationshipsToCopy.push(possibleDuplicateAssociation.relationshipId)
    }
    if (
      survivingPersonAssociation.relationshipId !== possibleDuplicateAssociation.relationshipId &&
      !state.mergedFieldIds.idsOfAssociationRelationshipsToDelete.includes(survivingPersonAssociation.relationshipId)
    ) {
      state.mergedFieldIds.idsOfAssociationRelationshipsToDelete.push(survivingPersonAssociation.relationshipId)
    }
  } else if (actionButtonType === actionButtonTypes.UNDO) {
    state.mergedFieldIds.idsOfAssociationRelationshipsToCopy =
      state.mergedFieldIds.idsOfAssociationRelationshipsToCopy.filter(
        (id) => id !== possibleDuplicateAssociation?.relationshipId
      )
    if (survivingPersonAssociation) {
      state.mergedFieldIds.idsOfAssociationRelationshipsToDelete =
        state.mergedFieldIds.idsOfAssociationRelationshipsToDelete.filter(
          (id) => id !== survivingPersonAssociation.relationshipId
        )
    }
  }
}

function addAllSourceReferences(sourceReferences, mergedFieldIds) {
  if (sourceReferences) {
    const sourceReferencesOfTypeSource = sourceReferences.filter((reference) => reference.value.type === 'SOURCE')
    sourceReferencesOfTypeSource.forEach((sourceRef) => {
      if (sourceRef.entityRefId) {
        mergedFieldIds.idsOfSourceReferencesToCopy.push(sourceRef.entityRefId)
      }
    })
  }
}
